/**
 * ADR-0016 — Inventory ledger quantities are always **base units**.
 *
 * Sell/purchase packs (carton, POS pack) are multipliers only. Conversion happens
 * at the write boundary when the caller supplies packaging input.
 */

export const QUANTITY_UOM_BASE = 'base';
export const QUANTITY_UOM_PACK = 'pack';
export const QUANTITY_UOM_CARTON = 'carton';

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function finiteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Multiplier: units per pack/carton.
 * @param {object} src
 * @returns {number}
 */
export function resolveUnitsPerPack(src = {}) {
  const candidates = [
    src.productsPerPackage,
    src.unitsPerPack,
    src.itemsPerBox,
    src.unitsPerCarton
  ];
  for (const c of candidates) {
    const n = Math.round(Number(c) || 0);
    if (n > 1) return n;
  }
  return 1;
}

/**
 * Pack/carton count input (unsigned magnitude when from pack qty).
 * @param {object} src
 * @returns {number|null}
 */
export function resolvePackCount(src = {}) {
  const candidates = [src.numberOfPackages, src.packCount, src.cartonCount];
  for (const c of candidates) {
    const n = finiteNumber(c);
    if (n != null && n > 0) return n;
  }
  return null;
}

/**
 * Catalog packaging stamp for audit when quantity is already base units (sale/transfer).
 * Does not convert quantity — only records unitsPerPack when known.
 *
 * @param {object} [src] — lot, product, or deduction line
 * @returns {{ unitsPerPack: number, quantityIsBase: true, sourceUom: string }|null}
 */
export function packagingAuditMetaFromCatalog(src = {}) {
  const unitsPerPack = resolveUnitsPerPack(src);
  if (!(unitsPerPack > 1)) return null;
  return {
    unitsPerPack,
    quantityIsBase: true,
    sourceUom: QUANTITY_UOM_BASE,
    packagingType: src.packagingType || src.packType || null
  };
}

/**
 * Convert packaging-oriented input to signed base-unit quantity.
 *
 * Conversion is **opt-in** to avoid double-counting legacy movements that
 * already store base qty and may still carry packagingType catalogue fields:
 * - `quantityUom: 'pack'|'carton'`, or
 * - `packagingInput: { numberOfPackages, productsPerPackage, … }`
 *
 * Default: treat `quantity` as base and stamp `quantityUom: 'base'`.
 *
 * @param {object} entry — movement-like or { quantity, packagingInput }
 * @returns {{
 *   quantity: number,
 *   quantityUom: typeof QUANTITY_UOM_BASE,
 *   converted: boolean,
 *   packaging?: { unitsPerPack: number, packCount: number, sourceUom: string }
 * }}
 */
export function resolveBaseUnitQuantity(entry = {}) {
  const raw =
    finiteNumber(entry.quantity ?? entry.quantitySigned) ??
    finiteNumber(entry.qty) ??
    0;
  const sign = raw < 0 ? -1 : 1;
  const absRaw = Math.abs(raw);

  const hasPackagingInput =
    entry.packagingInput != null && typeof entry.packagingInput === 'object';
  const packSrc = hasPackagingInput
    ? { ...entry, ...entry.packagingInput }
    : entry;

  let uom = String(
    entry.quantityUom ||
      entry.unitOfMeasure ||
      packSrc.quantityUom ||
      packSrc.unitOfMeasure ||
      ''
  )
    .trim()
    .toLowerCase();

  if (uom === 'unit' || uom === 'units' || uom === 'base_unit' || uom === 'each') {
    uom = QUANTITY_UOM_BASE;
  }
  if (uom === 'box' || uom === 'boxes') {
    uom = QUANTITY_UOM_CARTON;
  }

  if (uom === QUANTITY_UOM_BASE) {
    return {
      quantity: Number.isFinite(raw) ? raw : 0,
      quantityUom: QUANTITY_UOM_BASE,
      converted: false
    };
  }

  const wantsPack =
    uom === QUANTITY_UOM_PACK ||
    uom === QUANTITY_UOM_CARTON ||
    hasPackagingInput;

  if (!wantsPack) {
    // Catalogue packagingType alone is not enough — quantity is already base
    return {
      quantity: Number.isFinite(raw) ? raw : 0,
      quantityUom: QUANTITY_UOM_BASE,
      converted: false
    };
  }

  const unitsPerPack = resolveUnitsPerPack(packSrc);
  let packCount = resolvePackCount(packSrc);
  const sourceUom =
    uom === QUANTITY_UOM_PACK || uom === QUANTITY_UOM_CARTON
      ? uom
      : QUANTITY_UOM_CARTON;

  if (packCount == null) {
    packCount = absRaw;
  }

  if (!(unitsPerPack > 1) || !(packCount > 0)) {
    return {
      quantity: Number.isFinite(raw) ? raw : 0,
      quantityUom: QUANTITY_UOM_BASE,
      converted: false
    };
  }

  const baseAbs = Math.round(packCount * unitsPerPack);
  return {
    quantity: sign * baseAbs,
    quantityUom: QUANTITY_UOM_BASE,
    converted: true,
    packaging: {
      unitsPerPack,
      packCount,
      sourceUom
    }
  };
}

/**
 * Normalize a movement entry so quantity* fields are base units.
 * Mutates a shallow copy; original pack meta kept under `packaging`.
 *
 * @param {object} entry
 * @returns {object}
 */
export function stampMovementBaseUnits(entry = {}) {
  const resolved = resolveBaseUnitQuantity(entry);
  const next = {
    ...entry,
    quantity: resolved.quantity,
    quantitySigned: resolved.quantity,
    quantityUom: QUANTITY_UOM_BASE
  };
  if (resolved.converted && resolved.packaging) {
    next.packaging = {
      ...(entry.packaging && typeof entry.packaging === 'object' ? entry.packaging : {}),
      ...resolved.packaging,
      convertedToBase: true
    };
  }
  // Do not persist packagingInput on the movement row (would re-convert on replay)
  if ('packagingInput' in next) {
    delete next.packagingInput;
  }
  return next;
}

/**
 * Human-facing pack → base (unsigned).
 * @param {number} packCount
 * @param {number} unitsPerPack
 */
export function packsToBaseUnits(packCount, unitsPerPack) {
  const packs = Number(packCount);
  const per = Math.round(Number(unitsPerPack) || 0);
  if (!(packs > 0) || !(per > 1)) return null;
  return Math.round(packs * per);
}

/**
 * Base → whole packs (floor). Remainder in base units.
 */
export function baseUnitsToPacks(baseQty, unitsPerPack) {
  const base = Math.abs(Number(baseQty) || 0);
  const per = Math.round(Number(unitsPerPack) || 0);
  if (!(per > 1)) {
    return { packs: 0, remainder: base, unitsPerPack: per || 1 };
  }
  const packs = Math.floor(base / per);
  return { packs, remainder: base - packs * per, unitsPerPack: per };
}
